/**
 * SMBLoginForm component
 *
 * Login form for existing users in the SMB signup modal
 * Matches styling of SMBSignupForm for consistency
 * Supports two-factor authentication (2FA) for users with MFA enabled
 */
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import * as Sentry from '@sentry/react';
import { useCallback, useRef, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import login from '@/auth';
import VerificationCode from '@/auth/components/Login/VerificationCode';
import { PasswordTextField } from '@/auth/components/PasswordTextField';
import { handleCredentialsMissing } from '@/auth/utils/credentialsMissing';
import Button from '@/components/Button';
import Link from '@/components/Link';
import { ERROR_CODES, OtpMethod, UnauthorizedError } from '@/core/api';

import { SMBLoginFormProps } from './types';
import {
  calculateDuration,
  trackPhase1ForgotPasswordClicked,
  trackPhase1LoginFailed,
  trackPhase1LoginSubmitted,
  trackPhase1LoginSuccess,
  trackPhase1SwitchToSignupClicked,
} from './utils/analytics';

type LoginStep = 'credentials' | 'mfa';

interface OTPConfiguration {
  method?: OtpMethod;
  maskedAddress?: string;
}

const useStyles = makeStyles((theme: any) => ({
  formContainer: {
    // No flex centering - use specific alignment
  },
  title: {
    // Push down to align with the bottom margin area of left h1 title
    // Left h1: fontSize 26 (~31px with lineHeight) + marginBottom 16px = starts at ~31px
    marginTop: 15, // Reduced gap between copy text and Log In title
    marginBottom: 14, // Reduced to align email field with body text start
    fontSize: 28,
    fontWeight: 700,
    textAlign: 'center',
    lineHeight: 1.2,
  },
  field: {
    marginBottom: '12px', // Comfortable spacing between fields
    position: 'relative',
    '& .MuiOutlinedInput-root': {
      fontSize: 14,
      '& input': {
        padding: '12px 12px',
        fontSize: 14,
      },
      '& .MuiIconButton-root': {
        padding: 6,
        '& .MuiSvgIcon-root': {
          fontSize: 18, // Smaller password eye icon
        },
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: 13,
      transform: 'translate(14px, 12px) scale(1)', // Center label vertically
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -9px) scale(0.75)',
      },
    },
    '& .MuiInputLabel-asterisk': {
      display: 'none', // Hide asterisk
    },
  },
  button: {
    marginTop: theme.spacing(1),
    padding: '8px 16px', // Thinner button - same as signup
    minHeight: 36,
  },
  forgotPassword: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    textAlign: 'center',
    fontSize: 14,
  },
  loginDivider: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  signup: {
    textAlign: 'center',
    fontSize: 14,
  },
  signupLink: {
    background: 'none',
    border: 'none',
    color: 'var(--brandPrimary)', // Green button color; MUI primary stays dark in dark mode
    cursor: 'pointer',
    fontSize: 'inherit',
    padding: 0,
    fontWeight: 400, // Normal weight
    marginLeft: theme.spacing(0.5), // Space after question mark
    '&:hover': {
      opacity: 0.8,
    },
  },
  error: {
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
    fontSize: 14,
    textAlign: 'center',
  },
}));

/* eslint-disable react/prop-types */
export const SMBLoginForm: React.FC<SMBLoginFormProps> = ({
  onSuccess,
  onToggleView,
  handoff,
  expert,
  returnTo,
}) => {
  const s = useStyles();
  const [step, setStep] = useState<LoginStep>('credentials');
  const [loginEmail, setLoginEmail] = useState(handoff?.email ?? '');
  const [loginPassword, setLoginPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpConfiguration, setOtpConfiguration] = useState<OTPConfiguration>({});
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [buttonResendDisable, setButtonResendDisable] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const loginStartTime = useRef<Date>(new Date());
  const modalOpenTime = useRef<Date>(new Date());

  const handleLoginSubmit = useCallback(
    async (e?: React.FormEvent) => {
      if (e) {
        e.preventDefault();
      }

      if (!loginEmail || !loginPassword) {
        return;
      }

      // Track login submission
      const has2FA = step === 'mfa';
      trackPhase1LoginSubmitted(has2FA, expert);

      setIsLoggingIn(true);
      setLoginError(null);

      try {
        const recaptchaToken = executeRecaptcha ? await executeRecaptcha('smb_login') : '';
        // Track success before login() — it calls window.location.assign()
        // which destroys the execution context, so tracking after never fires.
        const durationToComplete = calculateDuration(modalOpenTime.current);
        trackPhase1LoginSuccess(has2FA, durationToComplete, expert);

        await login({
          email: loginEmail,
          password: loginPassword,
          otp: step === 'mfa' ? otp : undefined, // Include OTP when on MFA step
          next: returnTo || '/dashboard',
          recaptchaToken,
        });

        // User is now logged in, close modal (unreachable on success — login redirects)
        onSuccess('success');
      } catch (err: unknown) {
        setIsLoggingIn(false);

        const credentialsMissing = handleCredentialsMissing(err);
        if (credentialsMissing) {
          setLoginError(credentialsMissing);
          trackPhase1LoginFailed(
            'credentials_missing',
            ERROR_CODES.AUTH_CREDENTIALS_MISSING,
            step,
            expert
          );
        } else if (err instanceof UnauthorizedError) {
          let errorType = 'unknown';
          const errorCode: string | undefined = err.code;

          switch (err.code) {
            case ERROR_CODES.AUTH_INVALID_CREDENTIALS:
              errorType = 'invalid_credentials';
              setLoginError('Invalid email or password');
              trackPhase1LoginFailed(errorType, errorCode, step, expert);
              break;
            case ERROR_CODES.AUTH_LOCKED_FOR_INTERVAL:
              errorType = 'locked_account';
              setLoginError('Your account has been locked. Please reset your password.');
              trackPhase1LoginFailed(errorType, errorCode, step, expert);
              break;
            case ERROR_CODES.AUTH_MFA_REQUIRED:
              // Transition to MFA step (not an error)
              if (step !== 'mfa') {
                setOtpConfiguration({
                  method: err.otpMethod,
                  maskedAddress: err.otpMaskedAddress,
                });
                setStep('mfa');
                setLoginError(null);
              }
              break;
            case ERROR_CODES.AUTH_OTP_INVALID:
              errorType = 'mfa_failed';
              setLoginError('Invalid verification code');
              trackPhase1LoginFailed(errorType, errorCode, step, expert);
              break;
            case ERROR_CODES.AUTH_MFA_SETUP_REQUIRED:
              errorType = 'mfa_setup_required';
              setLoginError(
                'Your account requires two-factor authentication setup. Please use the main login page to complete setup.'
              );
              trackPhase1LoginFailed(errorType, errorCode, step, expert);
              break;
            default:
              setLoginError('An error occurred. Please try again.');
              trackPhase1LoginFailed(errorType, errorCode, step, expert);
          }
        } else {
          Sentry.captureException(err);
          setLoginError('An error occurred. Please try again.');
          trackPhase1LoginFailed('unknown', undefined, step, expert);
        }
      }
    },
    [loginEmail, loginPassword, otp, step, executeRecaptcha, onSuccess, returnTo, expert]
  );

  const handleOtpChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setOtp(e.target.value);
    },
    []
  );

  const handleCancel = useCallback(() => {
    setOtp('');
    setLoginError(null);
    setStep('credentials');
    setOtpConfiguration({});
    setIsLoggingIn(false);
    setButtonResendDisable(false);
  }, []);

  const handleResend = useCallback(async () => {
    setButtonResendDisable(true);
    try {
      const recaptchaToken = executeRecaptcha ? await executeRecaptcha('smb_login') : '';

      await login({
        email: loginEmail,
        password: loginPassword,
        next: returnTo || '/dashboard',
        recaptchaToken,
      });
    } catch (err: unknown) {
      if (!(err instanceof UnauthorizedError) || err.code !== ERROR_CODES.AUTH_MFA_REQUIRED) {
        Sentry.captureException(err);
        setButtonResendDisable(false);
      }
      // Success - code was resent (AUTH_MFA_REQUIRED is expected)
      setLoginError(null);
      return;
    }
    console.warn('Unexpected login success without OTP during resend');
  }, [executeRecaptcha, loginEmail, loginPassword, returnTo]);

  const isFormValid = loginEmail && loginPassword;
  const hasTypedOtp = otp.length > 0;

  // Track "Forgot password?" link click
  const handleForgotPasswordClick = useCallback(() => {
    trackPhase1ForgotPasswordClicked(expert);
  }, [expert]);

  // Track "Sign up" link click
  const handleSignUpClick = useCallback(() => {
    const timeOnView = calculateDuration(loginStartTime.current);
    trackPhase1SwitchToSignupClicked(timeOnView, expert);
    onToggleView();
  }, [onToggleView, expert]);

  return (
    <div className={s.formContainer}>
      {step === 'credentials' ? (
        <form onSubmit={handleLoginSubmit}>
          <h2 className={s.title}>Log In</h2>

          {handoff && (
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', textAlign: 'center', lineHeight: 1.5, mb: 2 }}
            >
              {handoff.notice}
            </Typography>
          )}

          <TextField
            variant="outlined"
            fullWidth
            type="email"
            label="Email"
            value={loginEmail}
            onChange={(e) => setLoginEmail(e.target.value)}
            required
            margin="none"
            className={s.field}
            inputProps={{ autoComplete: 'email' }}
          />

          <PasswordTextField
            endAdornmentId="smbLoginPasswordEye"
            variant="outlined"
            fullWidth
            label="Password"
            value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value)}
            required
            margin="none"
            className={s.field}
            inputProps={{ autoComplete: 'current-password' }}
          />

          <div className={s.forgotPassword}>
            <Link href="/password_reset" newTab onClick={handleForgotPasswordClick}>
              Forgot your password?
            </Link>
          </div>

          {loginError && <div className={s.error}>{loginError}</div>}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={!isFormValid || isLoggingIn}
            classes={{ root: s.button }}
          >
            {isLoggingIn ? 'Logging in...' : 'Log In'}
          </Button>

          <Divider className={s.loginDivider} />
          <div className={s.signup}>
            Don&apos;t have an account?
            <button type="button" className={s.signupLink} onClick={handleSignUpClick}>
              Sign up
            </button>
          </div>
        </form>
      ) : (
        // MFA verification step
        <VerificationCode
          code={otp}
          onChange={handleOtpChange}
          onSubmit={handleLoginSubmit}
          onCancel={handleCancel}
          onResend={handleResend}
          disabled={!hasTypedOtp}
          buttonResendDisable={buttonResendDisable}
          error={loginError || ''}
          method={otpConfiguration.method!}
          maskedAddress={otpConfiguration.maskedAddress!}
        />
      )}
    </div>
  );
};

export default SMBLoginForm;
